/**
 * Preference-aware date formatting utilities
 * 
 * These utilities format dates according to business preferences while maintaining
 * safe fallbacks. Use these for new code and gradually migrate existing code.
 * 
 * @typedef {import('../types/preferences').BusinessPreferences} BusinessPreferences
 */

import { format, parseISO, parse } from 'date-fns';

/**
 * Default date format to use when preferences are not available
 */
const DEFAULT_DATE_FORMAT = 'yyyy-MM-dd';
const DEFAULT_TIME_FORMAT = '12'; // 12 or 24

/**
 * Maps business preference date format to date-fns format tokens
 * @param {string} prefFormat - Format from preferences (e.g., 'YYYY-MM-DD', 'MM/DD/YYYY', 'DD/MM/YYYY')
 * @returns {string} - date-fns compatible format string
 */
export function mapPreferenceFormatToDateFns(prefFormat) {
    if (!prefFormat) return DEFAULT_DATE_FORMAT;
    
    // Map common preference formats to date-fns formats
    const formatMap = {
        'YYYY-MM-DD': 'yyyy-MM-dd',
        'MM/DD/YYYY': 'MM/dd/yyyy',
        'DD/MM/YYYY': 'dd/MM/yyyy',
        'DD-MM-YYYY': 'dd-MM-yyyy',
        'YYYY/MM/DD': 'yyyy/MM/dd',
        'MMM DD, YYYY': 'MMM dd, yyyy',
        'DD MMM YYYY': 'dd MMM yyyy',
        'MMMM DD, YYYY': 'MMMM dd, yyyy'
    };

    return formatMap[prefFormat] || prefFormat;
}

/**
 * Gets the appropriate time format based on preferences
 * @param {Partial<BusinessPreferences>} preferences - Business preferences object
 * @returns {string} - Time format string for date-fns
 */
function getTimeFormat(preferences) {
    const timeFormat = preferences?.timeFormat || DEFAULT_TIME_FORMAT;
    
    if (timeFormat === '24') {
        return 'HH:mm';
    }
    return 'hh:mm a'; // 12-hour with AM/PM
}

/**
 * Formats a date according to business preferences
 * 
 * **Safe Usage:**
 * - Returns empty string if date is null/undefined
 * - Uses fallback format if preferences are missing
 * - Handles both Date objects and ISO strings
 * - Won't throw errors on invalid dates (returns original or empty string)
 * 
 * @param {Date|string|null|undefined} date - Date to format (Date object or ISO string)
 * @param {Partial<BusinessPreferences>} [preferences] - Business preferences (optional)
 * @param {string} [fallbackFormat] - Override format to use if preferences missing
 * @returns {string} - Formatted date string or empty string if invalid
 * 
 * @example
 * // With preferences
 * const { preferences } = usePreferences();
 * formatDateWithPreferences('2025-12-13', preferences) 
 * // → "2025-12-13" (if dateFormat is "YYYY-MM-DD")
 * // → "12/13/2025" (if dateFormat is "MM/DD/YYYY")
 * 
 * @example
 * // Without preferences (uses default)
 * formatDateWithPreferences('2025-12-13') // → "2025-12-13"
 * 
 * @example
 * // With Date object
 * formatDateWithPreferences(new Date(), preferences)
 * 
 * @example
 * // Safe with null/undefined
 * formatDateWithPreferences(null, preferences) // → ""
 */
export function formatDateWithPreferences(date, preferences, fallbackFormat) {
    // Safety check: return empty string for null/undefined
    if (!date) return '';

    try {
        // Get the format from preferences or use fallback
        const prefDateFormat = preferences?.dateFormat;
        const dateFormat = mapPreferenceFormatToDateFns(
            fallbackFormat || prefDateFormat || DEFAULT_DATE_FORMAT
        );

        // Handle Date objects directly
        if (date instanceof Date) {
            if (isNaN(date.getTime())) return ''; // Invalid date
            return format(date, dateFormat);
        }

        // Handle ISO strings
        if (typeof date === 'string') {
            // For date-only display, extract just the date portion to avoid timezone shifts
            // If the string contains 'T' (datetime), take only the date part (yyyy-MM-dd)
            const dateOnly = date.includes('T') ? date.split('T')[0] : date;
            
            // Try parsing as ISO
            const parsedDate = parseISO(dateOnly);
            if (!isNaN(parsedDate.getTime())) {
                return format(parsedDate, dateFormat);
            }
            
            // If not ISO, return original string (safer than throwing)
            return date;
        }

        // Unknown type, return empty string
        return '';
    } catch (error) {
        console.warn('formatDateWithPreferences error:', error, 'for date:', date);
        return date?.toString() || '';
    }
}

/**
 * Formats a time according to business preferences
 * 
 * @param {Date|string|null|undefined} time - Time to format
 * @param {Partial<BusinessPreferences>} [preferences] - Business preferences
 * @returns {string} - Formatted time string or empty string if invalid
 * 
 * @example
 * formatTimeWithPreferences('14:30', preferences)
 * // → "02:30 PM" (if timeFormat is "12")
 * // → "14:30" (if timeFormat is "24")
 */
export function formatTimeWithPreferences(time, preferences) {
    if (!time) return '';

    try {
        const timeFormat = getTimeFormat(preferences);

        // Handle Date objects
        if (time instanceof Date) {
            if (isNaN(time.getTime())) return '';
            return format(time, timeFormat);
        }

        // Handle time strings (HH:mm or HH:mm:ss)
        if (typeof time === 'string') {
            // Try parsing as time string
            let parsedTime;
            if (time.includes('T')) {
                // ISO datetime string
                parsedTime = parseISO(time);
            } else {
                // Plain time string, parse with today's date
                parsedTime = parse(time, 'HH:mm', new Date());
            }
            
            if (!isNaN(parsedTime.getTime())) {
                return format(parsedTime, timeFormat);
            }
            
            return time;
        }

        return '';
    } catch (error) {
        console.warn('formatTimeWithPreferences error:', error, 'for time:', time);
        return time?.toString() || '';
    }
}

/**
 * Formats a date and time according to business preferences
 * 
 * @param {Date|string|null|undefined} datetime - DateTime to format
 * @param {Partial<BusinessPreferences>} [preferences] - Business preferences
 * @returns {string} - Formatted datetime string or empty string if invalid
 * 
 * @example
 * formatDateTimeWithPreferences('2025-12-13T14:30:00Z', preferences)
 * // → "2025-12-13 02:30 PM" (if dateFormat is "YYYY-MM-DD" and timeFormat is "12")
 */
export function formatDateTimeWithPreferences(datetime, preferences) {
    if (!datetime) return '';

    try {
        const prefDateFormat = preferences?.dateFormat;
        const dateFormat = mapPreferenceFormatToDateFns(prefDateFormat || DEFAULT_DATE_FORMAT);
        const timeFormat = getTimeFormat(preferences);
        const combinedFormat = `${dateFormat} ${timeFormat}`;

        // Handle Date objects
        if (datetime instanceof Date) {
            if (isNaN(datetime.getTime())) return '';
            return format(datetime, combinedFormat);
        }

        // Handle ISO strings
        if (typeof datetime === 'string') {
            const parsedDate = parseISO(datetime);
            if (!isNaN(parsedDate.getTime())) {
                return format(parsedDate, combinedFormat);
            }
            return datetime;
        }

        return '';
    } catch (error) {
        console.warn('formatDateTimeWithPreferences error:', error, 'for datetime:', datetime);
        return datetime?.toString() || '';
    }
}

/**
 * Format date in a "nice" readable format (e.g., "Dec 13, 2025")
 * Uses preferences if available, otherwise uses a friendly default
 * 
 * @param {Date|string|null|undefined} date - Date to format
 * @param {Partial<BusinessPreferences>} [preferences] - Business preferences
 * @returns {string} - Nicely formatted date or empty string
 * 
 * @example
 * formatNiceDate('2025-12-13', preferences) // → "Dec 13, 2025"
 */
export function formatNiceDate(date, preferences) {
    if (!date) return '';
    
    try {
        // Use a friendly format by default
        const friendlyFormat = 'MMM dd, yyyy';
        
        const parsedDate = typeof date === 'string' ? parseISO(date) : date;
        if (parsedDate instanceof Date && !isNaN(parsedDate.getTime())) {
            return format(parsedDate, friendlyFormat);
        }
        
        return '';
    } catch (error) {
        console.warn('formatNiceDate error:', error, 'for date:', date);
        return '';
    }
}

/**
 * Format date for display in a short format (e.g., "12/13/25" or "13-12-25")
 * based on preferences
 * 
 * @param {Date|string|null|undefined} date - Date to format
 * @param {Partial<BusinessPreferences>} [preferences] - Business preferences
 * @returns {string} - Short formatted date or empty string
 */
export function formatShortDate(date, preferences) {
    if (!date) return '';
    
    try {
        const prefDateFormat = preferences?.dateFormat || DEFAULT_DATE_FORMAT;
        let shortFormat;
        
        // Map long formats to short formats
        if (prefDateFormat.includes('MM/DD/YYYY')) {
            shortFormat = 'MM/dd/yy';
        } else if (prefDateFormat.includes('DD/MM/YYYY')) {
            shortFormat = 'dd/MM/yy';
        } else {
            shortFormat = 'yy-MM-dd';
        }
        
        const parsedDate = typeof date === 'string' ? parseISO(date) : date;
        if (parsedDate instanceof Date && !isNaN(parsedDate.getTime())) {
            return format(parsedDate, shortFormat);
        }
        
        return '';
    } catch (error) {
        console.warn('formatShortDate error:', error, 'for date:', date);
        return '';
    }
}
